// API Client for backend communication
import { getIdToken } from '$lib/stores/auth.svelte';
import type { BillingInfo } from '$lib/billing';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const API_ENDPOINT = `${API_BASE_URL}/api`;

// Helper function to handle API errors gracefully
async function handleApiError(response: Response, defaultMessage: string): Promise<never> {
	if (response.status === 429) {
		throw new Error('Trop de requêtes. Veuillez patienter quelques secondes avant de réessayer.');
	}
	try {
		const error = await response.json();
		throw new Error(error.error || defaultMessage);
	} catch (e) {
		// SyntaxError : corps non JSON (page HTML d'un 502 nginx…), jamais affiché tel quel.
		if (e instanceof Error && !(e instanceof SyntaxError) && e.message !== defaultMessage) {
			throw e;
		}
		throw new Error(`${defaultMessage} (${response.status})`);
	}
}

export interface CurriculumStructure {
	[cycle: string]: {
		name: string;
		levels: string[];
		domains: {
			[domain: string]: string[];
		};
		// Restriction de certaines matières à des niveaux (rentrée 2026 : le CP
		// et le CE1-CE2 ne suivent pas les mêmes programmes). null = tout le cycle.
		domain_levels?: {
			[domain: string]: string[] | null;
		};
		// Niveaux auxquels chaque sous-domaine a réellement un contenu officiel.
		// null = tout le cycle. Un sous-domaine hors de cette liste générerait une
		// fiche ancrée sur un autre texte que celui choisi.
		sub_topic_levels?: {
			[domain: string]: { [subDomain: string]: string[] | null };
		};
		// Sous-thèmes officiels par domaine/thème — suggestions d'intitulés de séquence.
		sub_topics?: {
			[domain: string]: { [theme: string]: string[] };
		};
		// Référence(s) BO par domaine ; plusieurs entrées quand le programme dépend du niveau.
		domain_sources?: {
			[domain: string]: { niveaux: string[] | null; bo: string }[];
		};
	};
}

export interface BoReference {
	bo: string;
	nouveau: boolean;
}

// Retrouve la référence BO applicable à un couple (domaine, niveau) dans un cycle.
export function resolveBoReference(
	cycleData: CurriculumStructure[string] | undefined,
	domain: string,
	level: string
): BoReference | null {
	const entries = cycleData?.domain_sources?.[domain];
	if (!entries || !level) return null;
	const entry = entries.find((e) => !e.niveaux || e.niveaux.includes(level));
	if (!entry) return null;
	const year = parseInt(entry.bo.match(/\b(20\d{2})\b/)?.[1] ?? '0', 10);
	return { bo: entry.bo, nouveau: year >= 2026 };
}

// Cycle contenant un niveau donné (PS→CM2), pour les pages qui raisonnent par niveau.
export function cycleForLevel(
	curriculum: CurriculumStructure | null,
	level: string
): CurriculumStructure[string] | undefined {
	if (!curriculum) return undefined;
	return Object.values(curriculum).find((c) => c.levels.includes(level));
}

export interface Seance {
	numero: number;
	duree: string;
	deroulement: string;
	materiel: string[];
}

export interface LexiqueTerm {
	terme: string;
	definition: string;
}

export interface Lexique {
	description: string;
	termes: LexiqueTerm[];
}

export interface SequenceData {
	titre: string;
	objectifs: string[];
	competences: string[];
	interdisciplinarite: string[];
	duree_totale: string;
	seances: Seance[];
	lexique: Lexique;
	// Notions proposées pour la séquence suivante (absentes des fiches antérieures au 27/10/2026)
	suites_possibles?: string[];
}

export interface Sequence {
	id: number;
	titre: string;
	level: string;
	domain: string;
	sub_domain: string;
	num_sessions: number;
	individual_percentage: number;
	materials_allowed: boolean;
	specifications: string;
	sequence_data: SequenceData;
	folder_id: number | null;
	created_at: string;
	updated_at: string;
}

export interface SequenceFolder {
	id: number;
	name: string;
	created_at: string;
}

export interface GenerateSequenceRequest {
	sequence_name: string;
	level: string;
	domain: string;
	sub_domain: string;
	num_sessions: number;
	individual_percentage?: number;
	materials_allowed?: boolean;
	specifications?: string;
	document_texte?: string;
	sub_domains?: string[];
	competences_imposees?: string[];
}

export interface GenerateSequenceResponse {
	id: number;
	sequence: SequenceData;
}

// Save onboarding cycle and level preference
export async function saveCyclePreference(
	cycle_preference: 'cycle1' | 'cycle2' | 'cycle3' | 'both',
	level_preference?: string
): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');

	const response = await fetch(`${API_ENDPOINT}/auth/cycle-preference`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`
		},
		body: JSON.stringify({ cycle_preference, level_preference })
	});

	if (!response.ok) {
		await handleApiError(response, 'Erreur lors de la sauvegarde des préférences');
	}
}

// Fetch curriculum structure
export async function getCurriculum(): Promise<CurriculumStructure> {
	const response = await fetch(`${API_ENDPOINT}/curriculum`);
	if (!response.ok) {
		throw new Error('Failed to fetch curriculum');
	}
	return response.json();
}

export interface CompetenceOfficielle {
	id: string;
	texte: string;
}

// C4b : pool de compétences officielles (verbatim BO) d'une sélection —
// nourrit l'encart « Le programme dit ». Public, disponible à flag on.
export async function getCompetences(
	level: string,
	domain: string,
	subDomains: string[]
): Promise<{ disponible: boolean; competences: CompetenceOfficielle[] }> {
	const params = new URLSearchParams({ level, domain, sub_domains: subDomains.join('|') });
	const response = await fetch(`${API_ENDPOINT}/sequences/competences?${params}`);
	if (!response.ok) {
		return { disponible: false, competences: [] };
	}
	return response.json();
}

export interface ExtractionPdf {
	texte: string;
	pages: number;
	tronque: boolean;
	nom: string;
}

// C2 : extrait le texte d'un PDF d'appui (Pro). Le fichier n'est pas conservé
// côté serveur ; le texte revient au client qui le relit avant génération.
export async function extrairePdf(fichier: File): Promise<ExtractionPdf> {
	const token = await getIdToken();
	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}
	const corps = new FormData();
	corps.append('fichier', fichier);
	const response = await fetch(`${API_ENDPOINT}/sequences/extraire-pdf`, {
		method: 'POST',
		headers,
		body: corps
	});
	if (!response.ok) {
		const erreur = await response.json().catch(() => ({}));
		throw new Error(erreur.error || 'Impossible de lire ce PDF.');
	}
	return response.json();
}

// Generate a new sequence
export async function generateSequence(
	data: GenerateSequenceRequest,
	suivi: Record<string, unknown> = {}
): Promise<GenerateSequenceResponse> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};

	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/generate`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		// Préserver l'objet complet pour un 403 (quota atteint)
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'sequences' });
			throw errorData;
		}
		const error = await response.json();
		throw new Error(error.message || error.error || 'Failed to generate sequence');
	}

	trackFunnelEvent('generation_completed', { feature: 'sequences', duree_ms: Math.round(performance.now() - debut), ...suivi });
	return response.json();
}

// Aperçu du plan émis par la génération en deux temps (référentiel v2)
export interface PlanSequence {
	competences: string[];
	seances: { numero: number; titre: string; duree: string; objectif: string }[];
}

// Génération en deux temps streamée (SSE) : `onPlan` est appelé dès que le
// plan est validé côté serveur. Si le flux est indisponible (flag référentiel
// v2 désactivé), repli transparent sur la génération classique.
export async function generateSequenceStream(
	data: GenerateSequenceRequest,
	onPlan?: (plan: PlanSequence) => void,
	suivi: Record<string, unknown> = {}
): Promise<GenerateSequenceResponse> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();

	const headers: HeadersInit = { 'Content-Type': 'application/json' };
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/generate-stream`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (response.status === 409) {
		return generateSequence(data, suivi);
	}
	if (!response.ok) {
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'sequences' });
			throw errorData;
		}
		const error = await response.json();
		throw new Error(error.message || error.error || 'Failed to generate sequence');
	}
	if (!response.body) {
		return generateSequence(data, suivi);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let resultat: GenerateSequenceResponse | null = null;

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		let separation;
		while ((separation = buffer.indexOf('\n\n')) !== -1) {
			const brut = buffer.slice(0, separation);
			buffer = buffer.slice(separation + 2);
			let event = 'message';
			let dataStr = '';
			for (const ligne of brut.split('\n')) {
				if (ligne.startsWith('event: ')) event = ligne.slice(7).trim();
				else if (ligne.startsWith('data: ')) dataStr += ligne.slice(6);
			}
			if (!dataStr) continue;
			const payload = JSON.parse(dataStr);
			if (event === 'plan') {
				onPlan?.(payload as PlanSequence);
			} else if (event === 'fiche') {
				resultat = payload as GenerateSequenceResponse;
			} else if (event === 'erreur') {
				throw new Error(payload.error || 'Échec de la génération');
			}
		}
	}

	if (!resultat) {
		throw new Error('La génération a été interrompue. Veuillez réessayer.');
	}
	trackFunnelEvent('generation_completed', { feature: 'sequences', duree_ms: Math.round(performance.now() - debut), ...suivi });
	return resultat;
}

// List all sequences
export async function listSequences(): Promise<Sequence[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences`, { headers });
	if (!response.ok) {
		throw new Error('Failed to fetch sequences');
	}
	return response.json();
}

// Get a specific sequence
export async function getSequence(id: number): Promise<Sequence> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/${id}`, { headers });
	if (!response.ok) {
		throw new Error('Failed to fetch sequence');
	}
	return response.json();
}

// Delete a sequence
export async function deleteSequence(id: number): Promise<void> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/${id}`, {
		method: 'DELETE',
		headers
	});
	if (!response.ok) {
		throw new Error('Failed to delete sequence');
	}
}

// Export sequence to Word document
export async function exportSequenceToWord(id: number): Promise<Blob> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/${id}/export/docx`, { headers });
	if (!response.ok) {
		throw new Error('Failed to export sequence');
	}
	return response.blob();
}

// Export une progression (payload complet) en Word éditable
export async function exportProgressionToWord(output: ProgressionOutput): Promise<Blob> {
	const token = await getIdToken();

	const headers: HeadersInit = { 'Content-Type': 'application/json' };
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/progressions/export/docx`, {
		method: 'POST',
		headers,
		body: JSON.stringify(output)
	});
	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.error || "Échec de l'export Word.");
	}
	return response.blob();
}

// Export une évaluation (payload complet) en Word éditable
export async function exportEvaluationToWord(output: EvaluationOutput): Promise<Blob> {
	const token = await getIdToken();

	const headers: HeadersInit = { 'Content-Type': 'application/json' };
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/evaluations/export/docx`, {
		method: 'POST',
		headers,
		body: JSON.stringify(output)
	});
	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.error || "Échec de l'export Word.");
	}
	return response.blob();
}

// Déclenche le téléchargement d'un blob côté navigateur
export function downloadBlob(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

// ============================================
// Sequence Folders API
// ============================================

// List all folders
export async function listSequenceFolders(): Promise<SequenceFolder[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/folders`, { headers });
	if (!response.ok) {
		throw new Error('Failed to fetch folders');
	}
	return response.json();
}

// Create a new folder
export async function createSequenceFolder(name: string): Promise<SequenceFolder> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/folders`, {
		method: 'POST',
		headers,
		body: JSON.stringify({ name })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to create folder');
	}
	return response.json();
}

// Rename a folder
export async function renameSequenceFolder(id: number, name: string): Promise<SequenceFolder> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/folders/${id}`, {
		method: 'PUT',
		headers,
		body: JSON.stringify({ name })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to rename folder');
	}
	return response.json();
}

// Delete a folder (sequences inside move to uncategorized)
export async function deleteSequenceFolder(id: number): Promise<void> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/folders/${id}`, {
		method: 'DELETE',
		headers
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to delete folder');
	}
}

// Move a sequence to a folder (or to uncategorized if folderId is null)
export async function moveSequenceToFolder(
	sequenceId: number,
	folderId: number | null
): Promise<Sequence> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/sequences/${sequenceId}/move`, {
		method: 'PUT',
		headers,
		body: JSON.stringify({ folder_id: folderId })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to move sequence');
	}
	return response.json();
}

// ─── Partage d'une séquence par lien public (/s/<jeton>) ──────────────────────

export interface SharedSequence {
	level: string;
	domain: string;
	sub_domain: string;
	sub_domains: string[];
	num_sessions: number;
	sequence_data: SequenceData;
	est_proprietaire: boolean;
}

/** Crée le lien public de la séquence, ou renvoie celui qui existe déjà. */
export async function shareSequence(sequenceId: number): Promise<string> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/sequences/${sequenceId}/partage`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'Le lien de partage n\'a pas pu être créé.');
	return (await response.json()).share_token;
}

export async function unshareSequence(sequenceId: number): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/sequences/${sequenceId}/partage`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'Le lien n\'a pas pu être désactivé.');
}

/** Lecture publique, sans compte. Le token, s'il existe, sert seulement à
 * reconnaître le propriétaire et à rattacher la consultation au visiteur. */
export async function getSharedSequence(shareToken: string): Promise<SharedSequence | null> {
	const token = await getIdToken();
	const response = await fetch(`${API_ENDPOINT}/partage/${encodeURIComponent(shareToken)}`, {
		headers: token ? { Authorization: `Bearer ${token}` } : {}
	});
	if (response.status === 404) return null;
	if (!response.ok) await handleApiError(response, 'La séquence n\'a pas pu être chargée.');
	return response.json();
}

/** Copie la séquence partagée dans le compte du visiteur. Gratuit : rien n'est
 * généré. `deja` : elle y était déjà (rechargement, ou c'est la sienne). */
export async function copySharedSequence(shareToken: string): Promise<{ id: number; deja: boolean }> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/partage/${encodeURIComponent(shareToken)}/copier`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'La séquence n\'a pas pu être ajoutée à vos séquences.');
	return response.json();
}

// ============================================
// Journal Types
// ============================================

export interface JournalEntry {
	id: number;
	user_id: number;
	date: string;
	start_time: string;
	end_time: string;
	class_level: string;
	domain: string;
	subdomain: string | null;
	title: string;
	duree: string | null;
	deroulement: string | null;
	materiels: string[] | null;
	post_notes: string | null;
	sequence_id: number | null;
	seance_numero: number | null;
	ai_generated: boolean;
	position: number;
	half_width: boolean;
	/** Cochée « faite » depuis le mode classe (ISO), null sinon. */
	done_at: string | null;
	created_at: string;
	updated_at: string;
}

export interface SchoolSchedule {
	id?: number;
	user_id?: number;
	day_start: string;
	day_end: string;
	morning_break_start: string | null;
	morning_break_end: string | null;
	lunch_start: string;
	lunch_end: string;
	afternoon_break_start: string | null;
	afternoon_break_end: string | null;
	school_days: number[];
}

export interface CreateJournalEntryRequest {
	date: string;
	start_time: string;
	end_time: string;
	class_level: string;
	domain: string;
	subdomain?: string;
	title: string;
	duree?: string;
	deroulement?: string;
	materiels?: string[];
	post_notes?: string;
	sequence_id?: number;
	seance_numero?: number;
	ai_generated?: boolean;
	position?: number;
	half_width?: boolean;
}

export interface CreateFromSequenceRequest {
	sequence_id: number;
	seance_numero: number;
	date: string;
	start_time: string;
	end_time: string;
	class_level: string;
	position?: number;
}

export interface GenerateJournalEntryRequest {
	date: string;
	start_time: string;
	end_time: string;
	class_level: string;
	domain: string;
	subdomain?: string;
	title: string;
	duree?: string;
	position?: number;
}

export interface ReorderEntryRequest {
	entry_id: number;
	new_date?: string;
	new_start_time?: string;
	new_end_time?: string;
	new_position?: number;
}

// ============================================
// Journal API Functions
// ============================================

// List journal entries with optional date range
export async function listJournalEntries(
	startDate?: string,
	endDate?: string
): Promise<JournalEntry[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const params = new URLSearchParams();
	if (startDate) params.append('start_date', startDate);
	if (endDate) params.append('end_date', endDate);

	const queryString = params.toString();
	const url = `${API_ENDPOINT}/journal/entries${queryString ? `?${queryString}` : ''}`;

	const response = await fetch(url, { headers });
	if (!response.ok) {
		await handleApiError(response, 'Échec du chargement des entrées');
	}
	return response.json();
}

// Create a new journal entry
export async function createJournalEntry(data: CreateJournalEntryRequest): Promise<JournalEntry> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		await handleApiError(response, 'Échec de la création');
	}
	return response.json();
}

// Get a single journal entry
export async function getJournalEntry(id: number): Promise<JournalEntry> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/${id}`, { headers });
	if (!response.ok) {
		await handleApiError(response, 'Entrée non trouvée');
	}
	return response.json();
}

// Update a journal entry
export async function updateJournalEntry(
	id: number,
	data: Partial<CreateJournalEntryRequest>
): Promise<JournalEntry> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/${id}`, {
		method: 'PUT',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		await handleApiError(response, 'Échec de la mise à jour');
	}
	return response.json();
}

// Delete a journal entry
export async function deleteJournalEntry(
	id: number
): Promise<{ message: string; entry: JournalEntry }> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/${id}`, {
		method: 'DELETE',
		headers
	});

	if (!response.ok) {
		await handleApiError(response, 'Échec de la suppression');
	}
	return response.json();
}

// Reorder/reschedule an entry
export async function reorderJournalEntry(data: ReorderEntryRequest): Promise<JournalEntry> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/reorder`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		await handleApiError(response, 'Échec du déplacement');
	}
	return response.json();
}

// Create entry from existing sequence session
export async function createEntryFromSequence(
	data: CreateFromSequenceRequest
): Promise<JournalEntry> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/from-sequence`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		await handleApiError(response, "Échec de l'import");
	}
	return response.json();
}

// Generate entry using AI
export async function generateJournalEntry(
	data: GenerateJournalEntryRequest
): Promise<JournalEntry> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/generate`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		if (response.status === 403) {
			// Propage le payload {error: 'limit_reached', used, limit} aux composants
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'journal' });
			throw errorData;
		}
		await handleApiError(response, 'Échec de la génération');
	}
	trackFunnelEvent('generation_completed', { feature: 'journal', duree_ms: Math.round(performance.now() - debut) });
	return response.json();
}

// Get school schedule
export async function getSchoolSchedule(): Promise<SchoolSchedule> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/schedule`, { headers });
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Échec du chargement de l'emploi du temps");
	}
	return response.json();
}

// Update school schedule
export async function updateSchoolSchedule(data: Partial<SchoolSchedule>): Promise<SchoolSchedule> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/schedule`, {
		method: 'PUT',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la mise à jour');
	}
	return response.json();
}

// Export journal to Word
export async function exportJournalToWord(startDate: string, endDate: string): Promise<Blob> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const params = new URLSearchParams({
		start_date: startDate,
		end_date: endDate
	});

	const response = await fetch(`${API_ENDPOINT}/journal/export/docx?${params}`, { headers });
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Échec de l'export");
	}
	return response.blob();
}

// Export single day to Word
export async function exportJournalDayToWord(date: string): Promise<Blob> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/export/day/${date}`, { headers });
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Échec de l'export");
	}
	return response.blob();
}

// Move entry to new date/time (convenience wrapper)
export async function moveJournalEntry(
	entryId: number,
	newDate: string,
	newStartTime: string
): Promise<JournalEntry> {
	return reorderJournalEntry({
		entry_id: entryId,
		new_date: newDate,
		new_start_time: newStartTime
	});
}

// Batch import from sequence
export interface BatchImportRequest {
	sequence_id: number;
	start_date: string;
	end_date: string;
	class_level: string;
	dry_run?: boolean;
}

export interface ImportPlacement {
	seance_numero: number;
	date: string;
	start_time: string;
	end_time: string;
	duree: string | null;
}

export interface BatchImportResponse {
	created: number;
	warnings: string[];
	entries: JournalEntry[];
	placements: ImportPlacement[];
}

export async function batchImportFromSequence(
	data: BatchImportRequest
): Promise<BatchImportResponse> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/batch-from-sequence`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Erreur lors de l'import");
	}

	return response.json();
}

// ============================================
// Gestes de masse (décaler, dupliquer, vider, reporter) + annulation
// ============================================

async function authedJson<T>(path: string, body: unknown, fallbackError: string): Promise<T> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}${path}`, {
		method: 'POST',
		headers,
		body: JSON.stringify(body)
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || fallbackError);
	}
	return response.json();
}

export interface ShiftResponse {
	moved: number;
	entries: JournalEntry[];
	undo: { changes: { id: number; date: string }[] };
}

export async function shiftJournalEntries(fromDate: string, weeks: number): Promise<ShiftResponse> {
	return authedJson('/journal/entries/shift', { from_date: fromDate, weeks }, 'Échec du décalage');
}

export async function bulkUpdateJournalDates(
	changes: { id: number; date: string }[]
): Promise<{ updated: number }> {
	return authedJson('/journal/entries/bulk-update-dates', { changes }, "Échec de l'annulation");
}

export interface DuplicateWeekResponse {
	created: number;
	skipped: number;
	entries: JournalEntry[];
	undo: { ids: number[] };
}

export async function duplicateJournalWeek(
	sourceWeekStart: string,
	targetWeekStart: string
): Promise<DuplicateWeekResponse> {
	return authedJson(
		'/journal/entries/duplicate-week',
		{ source_week_start: sourceWeekStart, target_week_start: targetWeekStart },
		'Échec de la duplication'
	);
}

export interface ClearDayResponse {
	deleted: number;
	undo: { entries: JournalEntry[] };
}

export async function clearJournalDay(date: string): Promise<ClearDayResponse> {
	return authedJson('/journal/entries/clear-day', { date }, 'Échec du vidage de la journée');
}

export async function bulkRestoreJournalEntries(
	entries: JournalEntry[]
): Promise<{ created: number; entries: JournalEntry[] }> {
	return authedJson('/journal/entries/bulk-restore', { entries }, 'Échec de la restauration');
}

export async function bulkDeleteJournalEntries(ids: number[]): Promise<{ deleted: number }> {
	return authedJson('/journal/entries/bulk-delete', { ids }, "Échec de l'annulation");
}

export interface PostponeResponse {
	entry: JournalEntry;
	previous: { date: string; start_time: string; end_time: string };
}

export async function postponeJournalEntry(id: number): Promise<PostponeResponse> {
	return authedJson(`/journal/entries/${id}/postpone`, {}, 'Échec du report');
}

export async function markJournalEntryDone(id: number, done: boolean): Promise<JournalEntry> {
	return authedJson(`/journal/entries/${id}/done`, { done }, "La séance n'a pas pu être cochée");
}

/** Séance n+1 de la séquence posée au prochain créneau (`sequence`), ou le
 * créneau trouvé pour une génération IA à faire par /generate (`ia`). */
export type PrepareNextResponse =
	| { mode: 'sequence'; deja_planifiee: boolean; entry: JournalEntry }
	| {
			mode: 'ia';
			creneau: { date: string; start_time: string; end_time: string };
			class_level: string;
			domain: string;
			subdomain: string | null;
			duree: string | null;
	  };

export async function prepareNextJournalEntry(id: number): Promise<PrepareNextResponse> {
	return authedJson(
		`/journal/entries/${id}/prepare-next`,
		{},
		"La séance suivante n'a pas pu être préparée"
	);
}

// ============================================
// School calendar (vacances, fériés, zones)
// ============================================

export interface SchoolVacation {
	label: string;
	zone: string;
	start: string; // YYYY-MM-DD, premier jour sans classe
	end: string; // YYYY-MM-DD, jour de reprise (exclu)
	annee: string;
}

export interface SchoolCalendarData {
	zones: string[];
	vacances: SchoolVacation[];
	rentrees: { zone: string; annee: string; date: string }[];
	feries: Record<string, Record<string, string>>;
	user_zone: string | null;
	/** Années scolaires couvertes par le jeu de données embarqué (ex. ["2025-2026", "2026-2027"]).
	 * Au-delà, vacances et périodes sont inconnues : l'UI doit le dire. */
	annees: string[];
	generated_at?: string;
}

export async function getSchoolCalendar(): Promise<SchoolCalendarData> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/school-calendar`, { headers });
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec du chargement du calendrier scolaire');
	}
	return response.json();
}

/** Plage sans classe saisie par le prof (zone « Personnalisé »). `end` = jour de reprise, exclu. */
export interface CustomVacationInput {
	label: string;
	start: string; // YYYY-MM-DD
	end: string; // YYYY-MM-DD
}

export async function updateCustomVacations(
	vacations: CustomVacationInput[]
): Promise<{ vacations: CustomVacationInput[] }> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/vacations`, {
		method: 'PUT',
		headers,
		body: JSON.stringify({ vacations })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Échec de l'enregistrement des vacances");
	}
	return response.json();
}

export async function updateSchoolZone(
	zone: string | null
): Promise<{ school_zone: string | null }> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/school-zone`, {
		method: 'PUT',
		headers,
		body: JSON.stringify({ zone })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la mise à jour de la zone');
	}
	return response.json();
}

// Fill a week with entries instantiated from the recurring timetable slots
export interface FillWeekResponse {
	created: number;
	entries: JournalEntry[];
}

export async function fillWeekFromTimetable(weekStart: string): Promise<FillWeekResponse> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/entries/from-timetable`, {
		method: 'POST',
		headers,
		body: JSON.stringify({ week_start: weekStart })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Impossible de remplir la semaine');
	}

	return response.json();
}

// ============================================
// Timetable Types
// ============================================

export interface TimetableSlot {
	id: number;
	user_id: number;
	day_of_week: number; // 0=Monday … 6=Sunday (weekend only if the teacher works it)
	period: number | null; // 1-5, ou null = emploi du temps de toute l'année
	start_time: string;
	end_time: string;
	domain: string;
	class_level: string;
	color: string | null;
	position: number;
	created_at: string;
	updated_at: string;
}

export interface CreateTimetableSlotRequest {
	day_of_week: number;
	start_time: string;
	end_time: string;
	domain: string;
	class_level: string;
	color?: string;
	position?: number;
}

// ============================================
// Timetable API Functions
// ============================================

// Get all timetable slots
/** Créneaux d'emploi du temps.
 * - `{}` : l'emploi du temps de l'année (celui qu'on édite par défaut)
 * - `{period: N}` : les créneaux propres à la période N (vide si non personnalisée)
 * - `{date}` : ceux applicables à cette date, avec repli sur l'année */
export async function getTimetable(
	options: { period?: number | null; date?: string } = {}
): Promise<TimetableSlot[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const params = new URLSearchParams();
	if (options.date) params.set('date', options.date);
	else if (options.period != null) params.set('period', String(options.period));
	const query = params.toString();

	const response = await fetch(`${API_ENDPOINT}/journal/timetable${query ? `?${query}` : ''}`, {
		headers
	});
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || "Échec du chargement de l'emploi du temps");
	}
	return response.json();
}

/** Périodes pour lesquelles un emploi du temps distinct existe. */
export async function getCustomisedTimetablePeriods(): Promise<number[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable/periods`, { headers });
	if (!response.ok) return [];
	const data = await response.json();
	return data.periods ?? [];
}

/** Supprime la personnalisation d'une période : elle reprend l'emploi du temps d'année. */
export async function resetTimetablePeriod(period: number): Promise<void> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable/period/${period}`, {
		method: 'DELETE',
		headers
	});
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la réinitialisation de la période');
	}
}

// Create a new timetable slot
export async function createTimetableSlot(
	data: CreateTimetableSlotRequest
): Promise<TimetableSlot> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable`, {
		method: 'POST',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la création');
	}
	return response.json();
}

// Update a timetable slot
export async function updateTimetableSlot(
	id: number,
	data: Partial<CreateTimetableSlotRequest>
): Promise<TimetableSlot> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable/${id}`, {
		method: 'PUT',
		headers,
		body: JSON.stringify(data)
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la mise à jour');
	}
	return response.json();
}

// Delete a timetable slot
export async function deleteTimetableSlot(id: number): Promise<void> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable/${id}`, {
		method: 'DELETE',
		headers
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la suppression');
	}
}

/** Remplace l'emploi du temps édité : celui de l'année (period null) ou d'une
 * période. Personnaliser une période n'efface jamais l'année. */
export async function batchUpdateTimetable(
	slots: CreateTimetableSlotRequest[],
	period: number | null = null
): Promise<TimetableSlot[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable/batch`, {
		method: 'POST',
		headers,
		body: JSON.stringify({ slots, period })
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec de la mise à jour');
	}
	const data = await response.json();
	return data.slots;
}

// Get timetable slots for a specific day
export async function getTimetableForDay(dayOfWeek: number): Promise<TimetableSlot[]> {
	const token = await getIdToken();

	const headers: HeadersInit = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_ENDPOINT}/journal/timetable/day/${dayOfWeek}`, { headers });
	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Échec du chargement');
	}
	return response.json();
}

// ─── Bulletin Generator ───────────────────────────────────────────────────────

// Un élève par appel. Soit un domaine et un à trois tons (`tones`), soit
// plusieurs domaines LSU et un seul ton (`tone`). `contexte` : synthèse des
// notions travaillées par domaine, relue par l'enseignant, jamais de donnée
// d'élève.
export interface BulletinInput {
	student_name: string;
	level: string;
	domains: string[];
	behavior: string;
	strengths?: string;
	improvements?: string;
	tones?: string[];
	tone?: string;
	contexte?: Record<string, string>;
}

export interface BulletinAppreciation {
	domain: string;
	tone: string;
	text: string;
	chars: number;
	max_chars: number;
	truncated: boolean;
}

export interface BulletinResult {
	appreciations: BulletinAppreciation[];
	used: number | null;
	limit: number | null;
}

export type BulletinPeriode = 'T1' | 'T2' | 'T3' | 'annee' | 'libre';

export interface BulletinSyntheseDomaine {
	code: string;
	label: string;
	texte: string;
	nb_seances: number;
	nb_sequences: number;
}

export interface BulletinSynthese {
	periode: { code: BulletinPeriode; label: string; start: string; end: string; approximatif: boolean; vide: boolean };
	has_journal: boolean;
	nb_seances: number;
	autres_niveaux: string[];
	domaines: BulletinSyntheseDomaine[];
}

/** Notions travaillées par domaine LSU, depuis le cahier journal et les séquences (CP à CM2). */
export async function getBulletinSynthese(
	level: string,
	periode: BulletinPeriode,
	dates?: { start: string; end: string }
): Promise<BulletinSynthese> {
	const token = await getIdToken();
	const headers: HeadersInit = {};
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const params = new URLSearchParams({ level, periode });
	if (periode === 'libre' && dates) {
		params.set('start', dates.start);
		params.set('end', dates.end);
	}
	const response = await fetch(`${API_ENDPOINT}/bulletin/synthese?${params}`, { headers });
	if (!response.ok) return handleApiError(response, 'Erreur lors de la lecture du cahier journal.');
	return response.json();
}

export async function getBulletinUsage(): Promise<{ used: number | null; limit: number | null }> {
	const token = await getIdToken();
	const headers: HeadersInit = { 'Content-Type': 'application/json' };
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const response = await fetch(`${API_ENDPOINT}/bulletin-usage`, { headers });
	if (!response.ok) return handleApiError(response, 'Erreur lors de la récupération du quota.');
	return response.json();
}

export async function generateBulletin(input: BulletinInput): Promise<BulletinResult> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();
	const headers: HeadersInit = { 'Content-Type': 'application/json' };
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const response = await fetch(`${API_ENDPOINT}/generate-bulletin`, {
		method: 'POST',
		headers,
		body: JSON.stringify(input)
	});
	if (!response.ok) {
		// Préserver l'objet complet pour un 403 (quota atteint)
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'bulletin' });
			throw errorData;
		}
		return handleApiError(response, 'Erreur lors de la génération du bulletin.');
	}
	trackFunnelEvent('generation_completed', { feature: 'bulletin', duree_ms: Math.round(performance.now() - debut) });
	return response.json();
}

// ─── Progression Annuelle ─────────────────────────────────────────────────────

export interface PeriodeProgression {
	numero: number;
	label: string;
	duree: string;
	competences: string[];
	notions_cles: string[];
	activites_suggerees: string[];
}

export interface ProgressionOutput {
	titre: string;
	niveau: string;
	matiere: string;
	annee_scolaire: string;
	periodes: PeriodeProgression[];
}

export interface ProgressionInput {
	niveau: string;
	matiere: string;
	priorites?: string;
}

export interface ProgressionResult {
	progression: ProgressionOutput;
	id: number | null;
	used: number | null;
	limit: number | null;
}

export interface SavedProgression {
	id: number;
	user_id: number;
	created_at: string;
	niveau: string;
	matiere: string;
	priorites: string | null;
	output: ProgressionOutput;
}

export async function generateProgression(input: ProgressionInput): Promise<ProgressionResult> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();
	const headers: HeadersInit = { 'Content-Type': 'application/json' };
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const response = await fetch(`${API_ENDPOINT}/progressions/generate`, {
		method: 'POST',
		headers,
		body: JSON.stringify(input)
	});
	if (!response.ok) {
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'progression' });
			throw errorData;
		}
		return handleApiError(response, 'Erreur lors de la génération de la progression.');
	}
	trackFunnelEvent('generation_completed', { feature: 'progression', duree_ms: Math.round(performance.now() - debut) });
	return response.json();
}

export async function getProgressions(): Promise<SavedProgression[]> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/progressions`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors du chargement des progressions.');
	}
	return response.json();
}

export async function deleteProgression(id: number): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/progressions/${id}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors de la suppression.');
	}
}

// ─── Générateur d'évaluations ─────────────────────────────────────────────────

export interface ExerciceEvaluation {
	numero: number;
	type: string;
	titre: string;
	consigne: string;
	contenu: string[];
	bareme: number;
	corrige: string[];
}

export interface EvaluationOutput {
	titre: string;
	niveau: string;
	matiere: string;
	notions: string;
	duree: number;
	systeme_notation: string;
	total_points: number;
	exercices: ExerciceEvaluation[];
	version_differenciee: { exercices: ExerciceEvaluation[] } | null;
	baremes_notation: {
		lettres: Record<string, string>;
		codes: Record<string, string>;
		ceintures: Record<string, string>;
	};
}

export interface EvaluationInput {
	niveau: string;
	matiere: string;
	notions: string;
	duree: number;
	nb_exercices: number;
	types_exercices: string[];
	difficulte: string;
	avec_corrige: boolean;
	systeme_notation: string;
	sequence_id?: number;
}

export interface EvaluationResult {
	evaluation: EvaluationOutput;
	id: number;
	used: number;
	limit: number | null;
}

export interface SavedEvaluation {
	id: number;
	user_id: number;
	created_at: string;
	niveau: string;
	matiere: string;
	notions: string;
	duree: number;
	nb_exercices: number;
	types_exercices: string[];
	difficulte: string;
	avec_corrige: boolean;
	systeme_notation: string;
	sequence_id: number | null;
	output: EvaluationOutput;
}

export async function generateEvaluation(input: EvaluationInput): Promise<EvaluationResult> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const headers: HeadersInit = {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${token}`
	};
	const response = await fetch(`${API_ENDPOINT}/evaluations/generate`, {
		method: 'POST',
		headers,
		body: JSON.stringify(input)
	});
	if (!response.ok) {
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'evaluation' });
			throw errorData;
		}
		return handleApiError(response, "Erreur lors de la génération de l'évaluation.");
	}
	trackFunnelEvent('generation_completed', { feature: 'evaluation', duree_ms: Math.round(performance.now() - debut) });
	return response.json();
}

export async function listEvaluations(): Promise<SavedEvaluation[]> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/evaluations`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors du chargement des évaluations.');
	}
	return response.json();
}

export async function deleteEvaluation(id: number): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/evaluations/${id}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors de la suppression.');
	}
}

// ─── Plan de classe ───────────────────────────────────────────────────────────

export interface PlanSeat {
	id: string;
	dcol: number;
	drow: number;
}
export interface PlanFurniture {
	id: string;
	type: 'desk';
	col: number;
	row: number;
	seats: PlanSeat[];
}
export interface PlanStudent {
	id: string;
	name: string;
}
export interface PlanLayoutData {
	grid: { cols: number; rows: number; cell: number };
	furniture: PlanFurniture[];
	students: PlanStudent[];
	assignments: Record<string, string>;
	constraints: { separate: [string, string][]; frontRow: string[] };
}
export interface SavedPlanDeClasse {
	id: number;
	user_id: number;
	name: string;
	layout_data: PlanLayoutData;
	created_at: string;
	updated_at: string;
}

export async function listPlansDeClasse(): Promise<SavedPlanDeClasse[]> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/classroom-plans`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors du chargement des plans de classe.');
	}
	return response.json();
}

export async function createPlanDeClasse(
	name: string,
	layoutData: PlanLayoutData
): Promise<SavedPlanDeClasse> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/classroom-plans`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ name, layout_data: layoutData })
	});
	if (!response.ok) {
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'plan_de_classe' });
			throw errorData;
		}
		return handleApiError(response, "Erreur lors de l'enregistrement du plan.");
	}
	trackFunnelEvent('generation_completed', { feature: 'plan_de_classe', duree_ms: Math.round(performance.now() - debut) });
	return response.json();
}

export async function updatePlanDeClasse(
	id: number,
	name: string,
	layoutData: PlanLayoutData
): Promise<SavedPlanDeClasse> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/classroom-plans/${id}`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ name, layout_data: layoutData })
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors de la mise à jour du plan.');
	}
	return response.json();
}

export async function deletePlanDeClasse(id: number): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/classroom-plans/${id}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		return handleApiError(response, 'Erreur lors de la suppression.');
	}
}

// ─── Fiches d'exercices à thème ────────────────────────────────────────────

export interface WorksheetExercise {
	// « □ » dans l'équation = case réponse à cet endroit (opération à trous)
	equation: string;
	answer: number | string; // string pour la conjugaison (« chantons »), le pluriel, « < »
	// Habillage de l'opération : calcul direct (absent), à trous, vrai/faux ;
	// en maternelle, l'illustration est l'exercice (pictogrammes répétés).
	format?: 'trou' | 'vraifaux' | 'denombrer' | 'collections' | 'completer' | 'suite' | 'lettres' | 'graphisme';
	pictos?: number | [number, number];
	cible?: number;
	mot?: string;
	lettre?: string;
	motif?: 'verticales' | 'horizontales' | 'ronds' | 'ponts' | 'vagues' | 'zigzag' | 'boucles' | 'creneaux' | 'spirales';
}

// Petit problème rédigé par le LLM autour d'un calcul du moteur : le texte est
// de l'IA, la réponse vient du moteur (vérifiée avant d'être servie).
export interface WorksheetProbleme {
	probleme: string;
	equation: string;
	answer: number | string;
}

export interface ColoriageAssignment {
	image_svg: string;
	// Pictogramme du thème pour l'en-tête (bibliothèque) ; sinon la scène sert deux fois.
	header_svg?: string | null;
	// true = le fournisseur d'image a échoué, la fiche porte l'illustration de
	// secours (exercices et corrigé intacts, aucun crédit consommé).
	image_fallback?: boolean;
	// true = exercices définitifs servis sans attendre l'illustration (texte libre).
	pending_image?: boolean;
	exercises: WorksheetExercise[];
	// Différenciation (élémentaire) : ★ soutien et ★★★ défi (★★ = exercises).
	versions?: { '1': WorksheetExercise[]; '3': WorksheetExercise[] } | null;
	// Maternelle : ce que représente le pictogramme (« citrouilles ») et le mot du thème.
	objets?: string;
	mot?: string;
	problemes?: WorksheetProbleme[];
	consigne?: string;
	// Ligne « programme officiel » imprimée sous le titre (peut être vide).
	programme?: string;
	theme: string;
	theme_key?: string | null;
	notion: string;
	variant: string;
	level: string;
	variant_label: string;
	idempotent: boolean;
	used: number;
	limit: number;
}

export interface ColoriageTheme {
	key: string;
	label: string;
	seasonal: boolean;
}

export interface ColoriageRecent {
	theme: string;
	theme_key: string | null;
	notion: string;
	variant: string;
	level: string;
	variant_label: string;
	created_at: string | null;
}

export interface ColoriageContext {
	level: string;
	level_source: string;
	themes: ColoriageTheme[];
	recent: ColoriageRecent[];
	used: number;
	limit: number;
}

export async function getColoriageContext(): Promise<ColoriageContext> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/coloriage/context`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) return handleApiError(response, 'Erreur de chargement.');
	return response.json();
}

export function coloriageThemeThumb(key: string): string {
	return `/coloriage/themes/${key}.png`;
}

export async function generateColoriage(params: {
	prompt?: string;
	theme_key?: string;
	notion: string;
	variant: string;
	level: string;
	// Texte libre : renvoie les exercices tout de suite, sans image ni crédit.
	preview_only?: boolean;
}): Promise<ColoriageAssignment> {
	const debut = performance.now(); // durée réelle, jointe à generation_completed
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	let response: Response;
	try {
		response = await fetch(`${API_ENDPOINT}/coloriage/generate`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
			body: JSON.stringify(params)
		});
	} catch {
		trackFunnelEvent('coloriage_generate_failed', { status: 0, reason: 'network' });
		throw new Error('Connexion impossible. Vérifie ta connexion et réessaie.');
	}
	if (!response.ok) {
		if (response.status === 403) {
			const errorData = await response.json();
			trackFunnelEvent('paywall_hit', { feature: 'coloriage' });
			throw errorData;
		}
		// Deux mois de 502 silencieux (juil.→sept. 2026) : tout échec est tracé.
		const reason = await response
			.clone()
			.json()
			.then((b: { error?: string }) => b?.error || 'http_error')
			.catch(() => 'http_error');
		trackFunnelEvent('coloriage_generate_failed', { status: response.status, reason });
		return handleApiError(response, 'Erreur lors de la génération de la fiche.');
	}
	if (!params.preview_only) trackFunnelEvent('generation_completed', { feature: 'coloriage', duree_ms: Math.round(performance.now() - debut) });
	return response.json();
}

// ─── Onboarding ────────────────────��──────────────────────────────────────────

export type OnboardingIntent =
	| 'cahier'
	| 'sequences'
	| 'progression'
	| 'bulletin'
	| 'evaluation'
	| 'plan_de_classe'
	| 'coloriage'
	| 'heures'
	| 'issr'
	| 'multi_usage';

export interface OnboardingStatus {
	intent: OnboardingIntent | null;
	cahier_step: number;
	sequences_step: number;
	onboarding_completed: boolean;
	trial_days_left: number;
	trial_ends_at: string | null;
	weeks_count: number;
	sequences_count: number;
	progressions_count: number;
	evaluations_count: number;
	bulletins_count: number;
	hours_saved: number;
	timetable_configured: boolean;
	account_status: 'trial_full' | 'trial_limited' | 'active_paid';
	show_activation_gate: boolean;
	activation_gate_shown: boolean;
}

export async function selectIntent(
	intent: OnboardingIntent
): Promise<{ success: boolean; redirect_to: string }> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/onboarding/select-intent`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ intent })
	});
	if (!response.ok) await handleApiError(response, "Erreur lors de la sélection de l'intent");
	return response.json();
}

export async function completeOnboardingStep(
	path: 'cahier' | 'sequences',
	step: 1 | 2 | 3
): Promise<{ success: boolean; cahier_step: number; sequences_step: number; completed: boolean }> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/onboarding/step-complete`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ path, step })
	});
	if (!response.ok) await handleApiError(response, "Erreur lors de la mise à jour de l'étape");
	return response.json();
}

export async function getOnboardingStatus(): Promise<OnboardingStatus> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/onboarding/status`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'Erreur lors du chargement du statut');
	return response.json();
}

export async function markActivationGateSeen(): Promise<void> {
	const token = await getIdToken();
	if (!token) return;
	await fetch(`${API_ENDPOINT}/onboarding/activation-gate-seen`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` }
	});
}

export async function setTrialLimited(): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/onboarding/set-trial-limited`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'Erreur lors de la mise à jour du statut');
}

export async function trackFunnelEvent(
	eventName: string,
	properties: Record<string, unknown> = {}
): Promise<void> {
	const token = await getIdToken();
	if (!token) return;
	await fetch(`${API_ENDPOINT}/onboarding/track-event`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ event_name: eventName, properties })
	}).catch(() => {});
}

/** Usage du sonomètre public. Connecté : sous son compte, comme tout event.
 * Anonyme (la plupart des visiteurs) : beacon sans identifiant, `/events/sonometre`. */
export async function trackSonometreUsed(
	action: 'micro' | 'demo' | 'plein_ecran' | 'minuteur'
): Promise<void> {
	const token = await getIdToken();
	if (token) return trackFunnelEvent('sonometre_used', { action });
	try {
		const corps = new Blob([JSON.stringify({ action })], { type: 'text/plain' });
		navigator.sendBeacon?.(`${API_ENDPOINT}/events/sonometre`, corps);
	} catch {
		// Mesure seulement : un échec ne gêne jamais l'outil.
	}
}

// ─── Abonnement Stripe ────────────────────────────────────────────────────────

export type BillingInterval = 'monthly' | 'annual';

// Crée la session de checkout. Si le compte a déjà un abonnement vivant, le
// backend refuse (409) et renvoie à la place l'URL où aller : la facture à
// régler ou le portail. Un second abonnement naîtrait au tarif du jour et
// ferait perdre à l'enseignant le prix de l'ancien — on ne le crée jamais.
export async function createCheckoutSession(
	priceId: string,
	billingInterval: BillingInterval
): Promise<{ url: string; redirected: boolean }> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');

	// Renoncer au paiement ramène sur la page d'où l'on vient (et non sur
	// /pricing, qui faisait perdre le fil) ; le layout lit `checkout=annule`.
	const retour = new URL(window.location.href);
	retour.searchParams.set('checkout', 'annule');

	const response = await fetch(`${API_ENDPOINT}/stripe/create-checkout-session`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ priceId, tier: 'pro', billingInterval, cancel_url: retour.toString() })
	});
	if (response.status === 409) {
		const data = await response.json().catch(() => ({}));
		if (data.url) return { url: data.url, redirected: true };
	}
	if (!response.ok) {
		await handleApiError(response, 'Erreur lors de la création de la session de paiement');
	}
	const { url } = await response.json();
	return { url, redirected: false };
}

// Ouvre directement le checkout Stripe (Pro mensuel) depuis un mur de quota.
// Le mur renvoyait vers /pricing : une étape de plus, et `checkout_started`
// n'était jamais attribué au mur (seuls pricing_page et activation_gate
// apparaissaient dans le funnel). Redirige vers Stripe en cas de succès.
export async function startProCheckout(feature: string, trigger = 'paywall'): Promise<void> {
	const priceId = import.meta.env.VITE_STRIPE_LAUNCH_PRO_PRICE_ID;
	if (!priceId) throw new Error("Ce plan n'est pas disponible pour le moment.");

	await trackFunnelEvent('checkout_started', {
		trigger,
		feature,
		billing_interval: 'monthly',
		price_id: priceId
	});

	const { url, redirected } = await createCheckoutSession(priceId, 'monthly');
	// Lu au retour de Stripe par /pricing pour le montant de conversion.
	if (!redirected) sessionStorage.setItem('checkoutBillingInterval', 'monthly');
	window.location.href = url;
}

// Portail de facturation Stripe (factures, carte, résiliation). Le client
// Stripe est celui du compte connecté, résolu côté serveur. `payment_method`
// ouvre directement l'écran de changement de carte.
export async function openBillingPortal(
	flow?: 'payment_method',
	source = 'abonnement'
): Promise<void> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');

	trackFunnelEvent('billing_portal_opened', { flow: flow ?? 'home', source });
	const response = await fetch(`${API_ENDPOINT}/stripe/create-portal-session`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(flow ? { flow } : {})
	});
	if (!response.ok) {
		await handleApiError(response, "Impossible d'ouvrir la gestion de l'abonnement.");
	}
	const { url } = await response.json();
	window.location.href = url;
}

// L'abonnement vivant du compte, relu chez Stripe par le backend à chaque appel.
export async function getBilling(): Promise<BillingInfo | null> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');

	const response = await fetch(`${API_ENDPOINT}/stripe/billing`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) {
		await handleApiError(response, 'Impossible de récupérer votre abonnement.');
	}
	const data = await response.json();
	return data.subscription ?? null;
}

export interface DashboardRecent {
	tool: string;
	title: string;
	subtitle: string;
	date: string;
	href: string;
}

export interface DashboardData {
	counts: Record<string, number>;
	recents: DashboardRecent[];
	rentree: {
		progression: boolean;
		sequences: boolean;
		journal: boolean;
		plan_de_classe: boolean;
	};
}

export async function getDashboard(): Promise<DashboardData> {
	const token = await getIdToken();
	if (!token) throw new Error('Non authentifié');
	const response = await fetch(`${API_ENDPOINT}/dashboard`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'Erreur lors du chargement du tableau de bord');
	return response.json();
}

// ---------------------------------------------------------------------------
// Consentements emailing
// ---------------------------------------------------------------------------

export interface ConsentState {
	marketing_consent: boolean;
	product_updates_consent: boolean;
	/** true = l'invitation post-valeur a déjà été affichée, ne pas reproposer. */
	prompt_shown: boolean;
}

export async function getConsent(): Promise<ConsentState> {
	const token = await getIdToken();
	const response = await fetch(`${API_ENDPOINT}/consent`, {
		headers: { Authorization: `Bearer ${token}` }
	});
	if (!response.ok) await handleApiError(response, 'Impossible de charger vos préférences email.');
	return response.json();
}

/**
 * Met à jour les consentements. La réponse est l'état RÉELLEMENT joignable :
 * si la personne s'est désabonnée depuis un email, le backend refuse le
 * réabonnement et renvoie `false` (voir backend/flask_api/listmonk.py).
 * Toujours réafficher le retour plutôt que l'état optimiste.
 */
export async function updateConsent(
	changes: { marketing_consent?: boolean; product_updates_consent?: boolean },
	source: 'settings' | 'prompt' = 'settings'
): Promise<ConsentState> {
	const token = await getIdToken();
	const response = await fetch(`${API_ENDPOINT}/consent`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ ...changes, source })
	});
	if (!response.ok) await handleApiError(response, "Impossible d'enregistrer vos préférences.");
	return response.json();
}

/** Marque l'invitation comme vue (oui comme non) : elle ne reviendra plus. */
export async function markConsentPromptSeen(): Promise<void> {
	const token = await getIdToken();
	await fetch(`${API_ENDPOINT}/consent/prompt-seen`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}` }
	});
}

// ---------------------------------------------------------------------------
// 108 heures — décompte annuel (flask_api/hours.py). Saisie pure, aucun appel IA.
// ---------------------------------------------------------------------------

export interface HoursCategorySummary {
	id: number;
	key: string;
	label: string;
	/** Volume du décret pour l'année, avant prorata. */
	annual_minutes: number;
	/** Volume du décret au prorata de la quotité. */
	regulatory_minutes: number;
	/** Volume attendu retenu : ajusté par le prof, sinon réglementaire. */
	target_minutes: number;
	done_minutes: number;
	remaining_minutes: number;
	percent: number | null;
}

export interface HoursTotal {
	target_minutes: number;
	done_minutes: number;
	remaining_minutes: number;
	percent: number | null;
}

export interface HoursSettings {
	school_year: string;
	quotite: number;
	/** Volumes attendus ajustés, en minutes par clé de catégorie. */
	target_overrides: Record<string, number>;
	nom: string;
	ecole: string;
	circonscription: string;
}

export interface HoursEntry {
	id: number;
	category_id: number;
	category_key: string;
	date: string;
	duration_minutes: number;
	title: string;
	subtype: string | null;
	comment: string | null;
}

export interface HoursSummary {
	school_year: string;
	settings: HoursSettings;
	subtypes: { equipe: string[] };
	categories: HoursCategorySummary[];
	total: HoursTotal;
	entries: HoursEntry[];
}

export interface HoursEntryInput {
	date: string;
	category_key: string;
	duration_minutes: number;
	title: string;
	subtype?: string | null;
	comment?: string | null;
}

export interface HoursImportRow {
	line: number;
	date: string | null;
	raw_date: string;
	category_key: string | null;
	raw_category: string;
	subtype: string | null;
	duration_minutes: number | null;
	raw_duration: string;
	title: string;
	comment: string | null;
	error: string | null;
}

export interface HoursImportPreview {
	rows: HoursImportRow[];
	valid: number;
	invalid: number;
}

async function hoursFetch(path: string, init: RequestInit = {}, defaultMessage: string) {
	const token = await getIdToken();
	const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const response = await fetch(`${API_ENDPOINT}/hours${path}`, { ...init, headers });
	if (!response.ok) await handleApiError(response, defaultMessage);
	return response.json();
}

export interface HoursYear {
	year: string;
	entries: number;
}

export async function getHoursYears(): Promise<{ years: HoursYear[]; current: string }> {
	return hoursFetch('/years', {}, 'Impossible de charger les années.');
}

export async function getHoursSummary(year: string): Promise<HoursSummary> {
	return hoursFetch(`/${year}/summary`, {}, 'Impossible de charger le décompte.');
}

export async function putHoursSettings(
	year: string,
	settings: Omit<HoursSettings, 'school_year'>
): Promise<HoursSettings> {
	return hoursFetch(
		`/${year}/settings`,
		{
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(settings)
		},
		"Impossible d'enregistrer les réglages."
	);
}

export async function createHoursEntry(data: HoursEntryInput): Promise<HoursEntry> {
	return hoursFetch(
		'/entries',
		{ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
		"Impossible d'enregistrer l'entrée."
	);
}

export async function updateHoursEntry(id: number, data: HoursEntryInput): Promise<HoursEntry> {
	return hoursFetch(
		`/entries/${id}`,
		{ method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
		"Impossible de modifier l'entrée."
	);
}

export async function deleteHoursEntry(id: number): Promise<void> {
	await hoursFetch(`/entries/${id}`, { method: 'DELETE' }, "Impossible de supprimer l'entrée.");
}

export async function previewHoursImport(file: File): Promise<HoursImportPreview> {
	const form = new FormData();
	form.append('file', file);
	return hoursFetch(
		'/import/preview',
		{ method: 'POST', body: form },
		'Impossible de lire le fichier.'
	);
}

export async function commitHoursImport(rows: HoursImportRow[]): Promise<{ imported: number }> {
	return hoursFetch(
		'/import/commit',
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ rows })
		},
		"L'import a échoué."
	);
}

// ISSR — suivi des remplacements (flask_api/issr.py). Saisie pure, aucun appel IA.
// Les montants sont calculés par le backend, jour par jour, avec le barème en
// vigueur à la date de chaque journée.

export interface IssrBand {
	min_km: number;
	max_km: number | null;
	amount_cents: number;
	step_km: number | null;
	step_amount_cents: number | null;
}

export interface IssrScale {
	effective_from: string;
	source: string;
	source_url: string | null;
	placeholder: boolean;
	half_day_percent: number;
	bands: IssrBand[];
}

export interface IssrMonth {
	month: string;
	days: number;
	amount_cents: number;
}

export interface IssrMissionInput {
	start_date: string;
	end_date: string;
	school: string;
	distance_km: number;
	day_part: 'full' | 'half';
	weekdays: number[];
}

export interface IssrMission extends IssrMissionInput {
	id: number;
	days: number;
	amount_cents: number;
	unpriced_days: number;
	months: IssrMonth[];
}

export interface IssrProfile {
	home_school: string;
	nom: string;
}

export interface IssrSummary {
	school_year: string;
	placeholder: boolean;
	scales: { effective_from: string; source: string; source_url: string | null }[];
	zone: string | null;
	profile: IssrProfile;
	schools: { school: string; distance_km: number }[];
	missions: IssrMission[];
	months: IssrMonth[];
	total: { days: number; amount_cents: number; unpriced_days: number };
}

async function issrFetch(path: string, init: RequestInit = {}, defaultMessage: string) {
	const token = await getIdToken();
	const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const response = await fetch(`${API_ENDPOINT}/issr${path}`, { ...init, headers });
	if (!response.ok) await handleApiError(response, defaultMessage);
	return response.json();
}

/** Le barème du jour, sans compte. `null` tant que le backend ne le sert pas (barème factice). */
export async function getIssrPublicScale(): Promise<IssrScale | null> {
	const response = await fetch(`${API_ENDPOINT}/issr/public/scale`);
	if (response.status === 503) return null;
	if (!response.ok) await handleApiError(response, 'Impossible de charger le barème.');
	return response.json();
}

export async function getIssrYears(): Promise<{ years: HoursYear[]; current: string }> {
	return issrFetch('/years', {}, 'Impossible de charger les années.');
}

export async function getIssrSummary(year: string): Promise<IssrSummary> {
	return issrFetch(`/${year}/summary`, {}, 'Impossible de charger vos remplacements.');
}

export async function putIssrProfile(profile: IssrProfile): Promise<IssrProfile> {
	return issrFetch(
		'/profile',
		{ method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile) },
		"Impossible d'enregistrer le profil."
	);
}

export async function createIssrMission(data: IssrMissionInput): Promise<IssrMission> {
	return issrFetch(
		'/missions',
		{ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
		"Impossible d'enregistrer la mission."
	);
}

export async function updateIssrMission(id: number, data: IssrMissionInput): Promise<IssrMission> {
	return issrFetch(
		`/missions/${id}`,
		{ method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
		'Impossible de modifier la mission.'
	);
}

export async function deleteIssrMission(id: number): Promise<void> {
	await issrFetch(`/missions/${id}`, { method: 'DELETE' }, 'Impossible de supprimer la mission.');
}
